import type { TicketScope } from './serviceCalls/types';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from '../contexts/TranslationContext';
import { useAuth } from '../contexts/AuthContext';
import { useRBAC } from '../contexts/RBACContext';
import { POI, POICategory } from '../types';
import { POIService } from '../services/POIService';
import { apiService } from '../services/ApiService';
import { exportRoomsCSV as sharedExportRoomsCSV } from '../services/locationSystemActions';
import {
  isPOIIfcLoaded as isPOIIfcLoadedUtil,
  getPOISourceFile,
  isPOIIfcLoading as isPOIIfcLoadingUtil
} from '../utils/POIFileUtils';
import { parseCustomData, deriveSubcategoryFromObjectType } from '../utils/POIFieldExtractor';
import { buildSyntheticRoomPoi } from '../utils/syntheticRoomPoi';
import { getIconForCategory } from '../constants/Categories';
import { convertApiPoiToComponent } from '../utils/POIConverter';
import { useToggleSet } from '../hooks/useToggleSet';
import { showErrorToast, showSuccessToast } from './shared/toast';
import { logger } from '../utils/log';

interface Location {
  id: string;
  asset_id: string;
  name: string;
  description?: string;
  parent_id?: string;
  type: string;
  position?: {
    x: number;
    y: number;
    z: number;
  };
  children?: Location[];
  created_at: string;
  source_file?: string;
  ifc_id?: number;      // IFC expressId or RVT dbId
  ifc_guid?: string;    // IFC GUID (null for RVT elements)
  is_custom?: boolean;   // Custom room defined by user
  geometry_polygon?: {   // Room polygon for custom rooms
    points: { x: number; y: number }[];
    minZ: number;
    maxZ: number;
  };
  elevation?: number;
  height?: number;
}

interface LocationsPageProps {
  onOpenTickets?: (scope: TicketScope) => void;
  onCreateTicket?: (scope: TicketScope) => void;
  assetId?: string;
  permissionLevel?: 'read' | 'write';
  onNavigateToViewer?: (poi: POI) => void;
  loadedIfcFiles?: string[]; // Array of currently loaded IFC filenames in viewer
  loadingIfcFiles?: string[]; // Array of IFC filenames currently being loaded
  focusedLocationId?: string | null; // Location ID to scroll to and highlight (from search)
  onFocusHandled?: () => void; // Callback when focus has been handled
  onRequestLoadModel?: (fileName: string) => void; // Callback to load an IFC model by filename
  onRequestExtractPolygons?: () => Promise<Map<string, { points: {x: number, y: number}[], minZ: number, maxZ: number, sourceFile: string, dbId: number }>>; // Callback to extract room polygons from viewer (multi-model support)
  onSwitchToViewer?: () => void; // Callback to switch to 3D viewer tab
  onStartCustomRoomDrawing?: (preSelectedStoreyId?: string) => void; // Callback to start custom room drawing in viewer
  onStartCustomStoreyDrawing?: () => void; // Callback to start custom storey drawing in viewer
  onEditCustomRoom?: (location: Location) => void; // Callback to edit custom room in viewer
  onEditCustomStorey?: (location: Location) => void; // Callback to edit custom storey in viewer
}

const LocationsPage: React.FC<LocationsPageProps> = ({ onOpenTickets, onCreateTicket, assetId, permissionLevel, onNavigateToViewer, loadedIfcFiles, loadingIfcFiles, focusedLocationId, onFocusHandled, onRequestLoadModel, onRequestExtractPolygons, onSwitchToViewer, onStartCustomRoomDrawing, onStartCustomStoreyDrawing, onEditCustomRoom, onEditCustomStorey }) => {
  const { colors } = useTheme();
  const { t, isRTL } = useTranslation();
  const { isLoading: authLoading } = useAuth();
  const { isAdmin: rbacIsAdmin, hasRole, loading: rbacLoading } = useRBAC();
  // Use RBAC for admin check (hybrid: supports both legacy and RBAC roles)
  const isAdmin = !authLoading && !rbacLoading && rbacIsAdmin;

  // RBAC-based permission check for edit operations
  // Only Data Creator can edit content; Site Admin manages permissions only
  const canEditContent = isAdmin || hasRole('data_creator', assetId) || permissionLevel === 'write';
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Search functionality
  const [searchTerm, setSearchTerm] = useState('');

  // Create location modal
  const [showCreateLocationModal, setShowCreateLocationModal] = useState(false);
  const [newLocationName, setNewLocationName] = useState('');
  const [newLocationDescription, setNewLocationDescription] = useState('');


  // Asset ID state management - resolved from prop or localStorage
  const [resolvedAssetId, setResolvedAssetId] = useState<string | null>(null);

  // POI management state
  const [showPOIModal, setShowPOIModal] = useState(false);
  const [poiModalMode, setPOIModalMode] = useState<'create' | 'assign'>('create');
  const [poiModalStep, setPOIModalStep] = useState<'choice' | 'create' | 'assign'>('choice');
  const [selectedLocationForPOI, setSelectedLocationForPOI] = useState<Location | null>(null);
  const [existingPOIs, setExistingPOIs] = useState<any[]>([]);
  const [locationPOIs, setLocationPOIs] = useState<Record<string, any[]>>({});
  const [isLoadingPOIs, setIsLoadingPOIs] = useState(false);

  // Create POI form data
  const [newPOIName, setNewPOIName] = useState('');
  const [newPOIDescription, setNewPOIDescription] = useState('');
  const [newPOICategory, setNewPOICategory] = useState<POICategory>(POICategory.STRUCTURE);
  const [newPOIType, setNewPOIType] = useState('');
  const [newPOIIcon, setNewPOIIcon] = useState('📍');
  const [newPOIColor, setNewPOIColor] = useState('#007bff');
  const [selectedExistingPOI, setSelectedExistingPOI] = useState<string>('');

  // Collapsible categories state for POI assignment - all collapsed by default
  const {
    set: collapsedCategories,
    toggle: toggleCategoryCollapse,
    reset: resetCollapsedCategories,
  } = useToggleSet<string>();

  // Custom storey creation modals
  const [showStoreySubMenu, setShowStoreySubMenu] = useState(false);
  const [showManualStoreyModal, setShowManualStoreyModal] = useState(false);
  const [manualStoreyName, setManualStoreyName] = useState('');
  const [manualStoreyBottom, setManualStoreyBottom] = useState('');
  const [manualStoreyTop, setManualStoreyTop] = useState('');
  const [showMergeStoreyModal, setShowMergeStoreyModal] = useState(false);
  const [selectedMergeStoreys, setSelectedMergeStoreys] = useState<Set<string>>(new Set());
  const [mergeStoreyName, setMergeStoreyName] = useState('');
  const [destroyOriginStoreys, setDestroyOriginStoreys] = useState(false);
  const [editStoreyDropdownId, setEditStoreyDropdownId] = useState<string | null>(null);
  const [editingStoreyLocation, setEditingStoreyLocation] = useState<Location | null>(null);

  // Set Location modal state
  const [showSetLocationModal, setShowSetLocationModal] = useState(false);
  const [availableProperties, setAvailableProperties] = useState<string[]>([]);
  const [selectedStoreyProperty, setSelectedStoreyProperty] = useState<string>('');
  const [selectedSpaceProperty, setSelectedSpaceProperty] = useState<string>('');
  const [isLoadingProperties, setIsLoadingProperties] = useState(false);
  const [isReassigning, setIsReassigning] = useState(false);
  const [reassignResult, setReassignResult] = useState<{ updated: number; unchanged: number; failed: number; total: number } | null>(null);

  // Property search filters for Set Location modal
  const [storeyPropertySearch, setStoreyPropertySearch] = useState('');
  const [spacePropertySearch, setSpacePropertySearch] = useState('');

  // Geolocation assignment state
  const [isAssigningByGeolocation, setIsAssigningByGeolocation] = useState(false);
  const [geolocationResult, setGeolocationResult] = useState<{
    assigned: number;
    multipleSpaces: number;
    storeyFallback: number;
    unassigned: number;
  } | null>(null);

  // Expanded spaces state for locations - all collapsed by default (empty set = all collapsed)
  const {
    set: expandedSpaces,
    toggle: toggleExpandedSpace,
    reset: resetExpandedSpaces,
  } = useToggleSet<string>();
  const { set: expandedFiles, toggle: toggleFileCollapse } = useToggleSet<string>();
  const { set: expandedPOIs, toggle: togglePOIExpand } = useToggleSet<string>();

  // Create Custom Location dropdown
  const [showCreateLocationDropdown, setShowCreateLocationDropdown] = useState(false);
  const createLocationDropdownRef = useRef<HTMLDivElement>(null);

  // Find the RVT source filename from the locations tree (for custom rooms that don't have source_file)
  const rvtSourceFile = useMemo(() => {
    const findRvtFile = (locs: Location[]): string | undefined => {
      for (const loc of locs) {
        if (loc.source_file?.toLowerCase().endsWith('.rvt')) return loc.source_file;
        if (loc.children) {
          const found = findRvtFile(loc.children);
          if (found) return found;
        }
      }
      return undefined;
    };
    return findRvtFile(locations);
  }, [locations]);

  // Helper function to check if a POI's source file (IFC or RVT) is currently loaded in viewer
  // fallbackSourceFile: optional source file to use if POI doesn't have one (e.g., from parent location)
  const isPOIIfcLoaded = (poi: POI | any, fallbackSourceFile?: string): boolean =>
    isPOIIfcLoadedUtil(poi, loadedIfcFiles, fallbackSourceFile);

  // Helper function to check if a POI's IFC file is currently loading
  // fallbackSourceFile: optional source file to use if POI doesn't have one
  const isPOIIfcLoading = (poi: POI | any, fallbackSourceFile?: string): boolean =>
    isPOIIfcLoadingUtil(poi, loadingIfcFiles, fallbackSourceFile);

  // Load locations from API - now using complete tree endpoint
  const loadLocations = async () => {
    let currentAssetId = assetId;
    if (!currentAssetId) {
      // Try to get the selected asset from local storage or API
      const storedAssetId = localStorage.getItem('selectedAssetId');
      if (!storedAssetId) {
        setError(t('noAssetSelectedMessage'));
        setLoading(false);
        return;
      }
      currentAssetId = storedAssetId;
    }

    // Set the resolved asset ID
    setResolvedAssetId(currentAssetId);

    try {
      setLoading(true);
      setError(null);

      // Fetch complete location tree with POIs, counts, and categorization
      // Backend now returns the complete tree structure with POIs and categorization!
      const rootLocations = await apiService
        .get(`/assets/${currentAssetId}/locations/tree`)
        .catch(() => { throw new Error(t('failedToFetchLocations')); });

      logger.debug('🌳 Received complete tree from backend:', {
        rootCount: rootLocations.length,
        firstRoot: rootLocations[0] ? {
          id: rootLocations[0].id,
          name: rootLocations[0].name,
          type: rootLocations[0].type,
          childrenCount: rootLocations[0].children?.length || 0,
          poiCount: rootLocations[0].poiCount
        } : null
      });

      setLocations(rootLocations);

      // Start with hierarchy collapsed
      setExpandedNodes(new Set<string>());
    } catch (err) {
      console.error('Failed to load locations:', err);
      setError(err.message || 'Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  // Resolve asset ID when component mounts or assetId prop changes
  useEffect(() => {
    if (assetId) {
      setResolvedAssetId(assetId);
    } else {
      const storedAssetId = localStorage.getItem('selectedAssetId');
      if (storedAssetId) {
        setResolvedAssetId(storedAssetId);
      }
    }
  }, [assetId]);

  useEffect(() => {
    if (resolvedAssetId) {
      loadLocations();
    }
    // Load once per asset. loadLocations is re-created every render and sets the state
    // it loads, so depending on it would re-fetch the tree on its own result.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedAssetId]);

  // Close Create Custom Location dropdown on outside click
  useEffect(() => {
    if (!showCreateLocationDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (createLocationDropdownRef.current && !createLocationDropdownRef.current.contains(e.target as Node)) {
        setShowCreateLocationDropdown(false);
        setShowStoreySubMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCreateLocationDropdown]);

  // Close Edit Storey dropdown on outside click
  useEffect(() => {
    if (!editStoreyDropdownId) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-edit-storey-dropdown]')) {
        setEditStoreyDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [editStoreyDropdownId]);

  // Helper function to find ancestor path to a location
  const findLocationPath = (locs: Location[], targetId: string, path: string[] = []): string[] | null => {
    for (const loc of locs) {
      if (loc.id === targetId) {
        return path;
      }
      if (loc.children && loc.children.length > 0) {
        const result = findLocationPath(loc.children, targetId, [...path, loc.id]);
        if (result) return result;
      }
    }
    return null;
  };

  // Handle external focus request (from search navigation)
  useEffect(() => {
    if (focusedLocationId && locations.length > 0) {
      // Find the ancestor path to the target location
      const ancestorPath = findLocationPath(locations, focusedLocationId, []);

      if (ancestorPath !== null) {
        // Expand only the ancestor path to the target (all others stay collapsed)
        resetExpandedSpaces([...ancestorPath, focusedLocationId]);

        // Only expand the ancestor path (fresh set, not adding to previous)
        setExpandedNodes(new Set(ancestorPath));
      }

      // Scroll to and highlight the location after expansion settles
      setTimeout(() => {
        const element = document.getElementById(`location-${focusedLocationId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add('location-focused');
          // Remove highlight after animation
          setTimeout(() => element.classList.remove('location-focused'), 3000);
          // Only clear focus AFTER successful scroll
          onFocusHandled?.();
        }
        // If element not found, don't clear focus - locations may still be loading
        // The useEffect will re-run when locations updates
      }, 200);
    }
    // Keyed on the focus request itself. findLocationPath and the onFocusHandled prop are
    // re-created every render, and this effect writes a fresh Set into expandedNodes —
    // re-running on them would scroll, re-expand and re-render without end.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedLocationId, locations]);

  const handleDeleteLocation = async (locationId: string) => {
    if (!confirm('Are you sure you want to delete this location and all its children?')) {
      return;
    }

    try {
      await apiService.delete(`/assets/${resolvedAssetId}/locations/${locationId}`);

      // Reload locations after deletion
      await loadLocations();
    } catch (err) {
      console.error('Failed to delete location:', err);
      showErrorToast(`Failed to delete location: ${err.message}`);
    }
  };

  // Save handler for manual storey creation
  const handleSaveManualStorey = async () => {
    const bottom = parseFloat(manualStoreyBottom);
    const top = parseFloat(manualStoreyTop);
    if (!manualStoreyName.trim() || isNaN(bottom) || isNaN(top) || bottom >= top) return;

    try {
      const payload = {
        name: manualStoreyName.trim(),
        type: 'Building Storey',
        is_custom: true,
        parent_id: null,
        elevation: bottom,
        height: top - bottom,
        position_x: 0,
        position_y: 0,
        position_z: bottom
      };

      if (editingStoreyLocation) {
        await apiService.put(`/assets/${resolvedAssetId}/locations/${editingStoreyLocation.id}`, payload);
      } else {
        await apiService.post(`/assets/${resolvedAssetId}/locations`, payload);
      }
      setShowManualStoreyModal(false);
      setManualStoreyName('');
      setManualStoreyBottom('');
      setManualStoreyTop('');
      setEditingStoreyLocation(null);
      await loadLocations();
    } catch (err) {
      console.error('Failed to save storey:', err);
      showErrorToast('Failed to save storey. Please try again.');
    }
  };

  // Save handler for merge storeys
  const handleSaveMergeStorey = async () => {
    if (selectedMergeStoreys.size < 2 || !mergeStoreyName.trim()) return;

    const storeys = locations.filter(l => l.type === 'Building Storey' && selectedMergeStoreys.has(l.id));
    const bottom = Math.min(...storeys.map(s => parseFloat(String(s.elevation || 0))));
    const top = Math.max(...storeys.map(s => parseFloat(String(s.elevation || 0)) + parseFloat(String(s.height || 0))));

    try {
      // Create merged storey
      await apiService.post(`/assets/${resolvedAssetId}/locations`, {
        name: mergeStoreyName.trim(),
        type: 'Building Storey',
        is_custom: true,
        parent_id: null,
        elevation: bottom,
        height: top - bottom,
        position_x: 0,
        position_y: 0,
        position_z: bottom
      });

      // Delete origin storeys if requested
      if (destroyOriginStoreys) {
        for (const storeyId of selectedMergeStoreys) {
          await apiService.delete(`/assets/${resolvedAssetId}/locations/${storeyId}`);
        }
      }

      setShowMergeStoreyModal(false);
      setSelectedMergeStoreys(new Set());
      setMergeStoreyName('');
      setDestroyOriginStoreys(false);
      await loadLocations();
    } catch (err) {
      console.error('Failed to merge storeys:', err);
      showErrorToast('Failed to merge storeys. Please try again.');
    }
  };

  const handleCreateLocation = async () => {
    if (!newLocationName.trim()) {
      showErrorToast('Please enter a location name');
      return;
    }

    try {
      await apiService.post(`/assets/${resolvedAssetId}/locations`, {
        name: newLocationName.trim(),
        type: 'Building', // Default to Building for top-level locations
        parent_id: null, // Top-level location
        description: newLocationDescription.trim() || '',
        position_x: 0,
        position_y: 0,
        position_z: 0
      });

      // Close modal and reload locations
      setShowCreateLocationModal(false);
      setNewLocationName('');
      setNewLocationDescription('');
      await loadLocations();
    } catch (err) {
      console.error('Failed to create location:', err);
      showErrorToast(`Failed to create location: ${err.message}`);
    }
  };

  // Rooms CSV export — the builder/download now lives in services/locationSystemActions so the
  // in-viewer Browser pane produces a byte-identical export from the same location tree.
  const exportRoomsCSV = () => { sharedExportRoomsCSV(locations as any); };

  const handleDeleteAllLocations = async () => {
    if (!resolvedAssetId) {
      showErrorToast('No asset selected');
      return;
    }

    if (!confirm('Are you sure you want to delete ALL locations for this asset? This cannot be undone.')) {
      return;
    }

    try {
      await apiService.delete(`/assets/${resolvedAssetId}/locations`);

      // Reload locations after deletion
      await loadLocations();
      showSuccessToast('All locations deleted successfully');
    } catch (err) {
      console.error('Failed to delete all locations:', err);
      showErrorToast(`Failed to delete all locations: ${err.message}`);
    }
  };

  // Set Location Functions
  const handleOpenSetLocationModal = async () => {
    setShowSetLocationModal(true);
    setReassignResult(null);
    setSelectedStoreyProperty('');
    setSelectedSpaceProperty('');
    setStoreyPropertySearch('');
    setSpacePropertySearch('');
    await loadAvailableProperties();
  };

  const loadAvailableProperties = async () => {
    if (!resolvedAssetId) return;

    try {
      setIsLoadingProperties(true);
      const data = await apiService.get(`/assets/${resolvedAssetId}/pois/available-properties`);
      setAvailableProperties(data.properties || []);

      // Auto-select "Level" if available (common storey property)
      if (data.properties.includes('Level')) {
        setSelectedStoreyProperty('Level');
      }
    } catch (error) {
      console.error('Failed to load available properties:', error);
      setAvailableProperties([]);
    } finally {
      setIsLoadingProperties(false);
    }
  };

  const handleReassignLocations = async () => {
    if (!resolvedAssetId || (!selectedStoreyProperty && !selectedSpaceProperty)) {
      showErrorToast('Please select at least one property');
      return;
    }

    try {
      setIsReassigning(true);
      setReassignResult(null);

      const result = await apiService.post(`/assets/${resolvedAssetId}/pois/reassign-locations`, {
        storeyProperty: selectedStoreyProperty || undefined,
        spaceProperty: selectedSpaceProperty || undefined
      });
      setReassignResult(result);

      // Reload locations and POIs to reflect changes
      await loadLocations();
      await loadPOIs();

    } catch (error) {
      console.error('Failed to reassign locations:', error);
      showErrorToast(`Failed to reassign locations: ${error.message}`);
    } finally {
      setIsReassigning(false);
    }
  };

  // Handle geolocation-based assignment
  const handleAssignByGeolocation = async () => {
    if (!resolvedAssetId) return;

    try {
      setIsAssigningByGeolocation(true);
      setGeolocationResult(null);

      // Step 1: Check polygon status - how many spaces need polygon extraction
      // (status unavailable → skip extraction and proceed with cached polygons)
      const polygonStatus = await apiService
        .get(`/assets/${resolvedAssetId}/locations/polygon-status`)
        .catch(() => null);
      if (polygonStatus) {
        const { missingCount, totalSpaces, withPolygons } = polygonStatus;
        logger.debug(`[LocationsPage] Polygon status: ${withPolygons}/${totalSpaces} spaces have polygons, ${missingCount} missing`);

        // Step 2: Extract missing polygons if viewer has model loaded (multi-model support)
        if (missingCount > 0 && onRequestExtractPolygons) {
          logger.debug('[LocationsPage] Extracting room polygons from viewer (multi-model)...');
          try {
            const polygons = await onRequestExtractPolygons();

            if (polygons.size > 0) {
              // Cache extracted polygons to database
              // Multi-model format: includes sourceFile and dbId for proper matching
              const polygonData: Record<string, { points: {x: number, y: number}[], minZ: number, maxZ: number, sourceFile: string, dbId: number }> = {};
              polygons.forEach((polygon, key) => {
                // Key format is "sourceFile:dbId" but we use full polygon object which has dbId and sourceFile
                polygonData[key] = {
                  points: polygon.points,
                  minZ: polygon.minZ,
                  maxZ: polygon.maxZ,
                  sourceFile: polygon.sourceFile,
                  dbId: polygon.dbId
                };
              });

              logger.debug(`[LocationsPage] Caching ${polygons.size} extracted polygons (multi-model format)...`);
              await apiService.post(`/assets/${resolvedAssetId}/locations/cache-polygons`, { polygons: polygonData });
            }
          } catch (extractErr) {
            console.warn('[LocationsPage] Polygon extraction failed, continuing with cached data:', extractErr);
          }
        }
      }

      // Step 3: Perform geolocation assignment (uses cached polygons)
      const result = await apiService.post(`/assets/${resolvedAssetId}/pois/assign-by-geolocation`, {});
      setGeolocationResult(result.stats);

      // Reload locations and POIs to reflect changes
      await loadLocations();
      await loadPOIs();

    } catch (error) {
      console.error('Failed to assign by geolocation:', error);
      showErrorToast(`Failed to assign by geolocation: ${(error as Error).message}`);
    } finally {
      setIsAssigningByGeolocation(false);
    }
  };

  // POI Management Functions
  const loadPOIs = async () => {
    if (!resolvedAssetId) return;

    try {
      setIsLoadingPOIs(true);
      const rawPOIs = await apiService.get(`/assets/${resolvedAssetId}/pois`);
      logger.debug(`✅ loadPOIs got ${rawPOIs.length} POIs for asset ${resolvedAssetId}`, rawPOIs.slice(0, 2));

      // Convert API data to POI format (same as SystemsPage, via shared util)
      const convertedPOIs = rawPOIs.map((apiPoi: any) => ({
        ...convertApiPoiToComponent(apiPoi),
        title: apiPoi.title, // Keep title for backward compatibility
        // Keep original API fields for backward compatibility
        subcategory: apiPoi.subcategory,
        poi_type_name: apiPoi.poi_type_name,
        custom_data: apiPoi.custom_data,
        location_id: apiPoi.location_id
      }));

      setExistingPOIs(convertedPOIs);

      // Create a lookup map for POIs by their numeric ID
      const poiById: Record<number, POI> = {};
      for (const poi of convertedPOIs) {
        poiById[poi.id] = poi;
      }

      // Load POI assignments for each location
      const locationPOIMap: Record<string, POI[]> = {};

      // First, load assignments from junction table (poi_locations) for geolocation
      try {
        const junctionAssignments = await apiService.get(`/assets/${resolvedAssetId}/poi-location-assignments`);
        for (const assignment of junctionAssignments) {
          const poi = poiById[assignment.poi_id];
          if (poi) {
            if (!locationPOIMap[assignment.location_id]) {
              locationPOIMap[assignment.location_id] = [];
            }
            // Avoid duplicates - a POI can appear under multiple locations
            if (!locationPOIMap[assignment.location_id].find(p => p.id === poi.id)) {
              locationPOIMap[assignment.location_id].push(poi);
            }
          }
        }
      } catch (error) {
        console.warn('Failed to load junction table assignments:', error);
      }

      // Then, add assignments from direct location_id field (legacy/manual)
      for (const poi of convertedPOIs) {
        let locationId = null;

        // First try to get location_id from the direct API field (new approach)
        if ((poi as any).location_id) {
          locationId = (poi as any).location_id;
        }
        // Fallback to legacy custom_data approach for backward compatibility
        else if (poi.custom_data) {
          locationId = parseCustomData(poi)?.location_id;
        }

        if (locationId) {
          if (!locationPOIMap[locationId]) {
            locationPOIMap[locationId] = [];
          }
          // Avoid duplicates
          if (!locationPOIMap[locationId].find(p => p.id === poi.id)) {
            locationPOIMap[locationId].push(poi);
          }
        }
      }
      setLocationPOIs(locationPOIMap);

      // No need to reload locations anymore - backend /tree endpoint includes everything!
    } catch (error) {
      console.error('Failed to load POIs:', error);
    } finally {
      setIsLoadingPOIs(false);
    }
  };

  const handleAddPOI = (location: Location) => {
    setSelectedLocationForPOI(location);
    resetPOIForm();
    setShowPOIModal(true);
  };

  const resetPOIForm = () => {
    setNewPOIName('');
    setNewPOIDescription('');
    setNewPOICategory(POICategory.STRUCTURE);
    setNewPOIType('');
    setNewPOIIcon('📍');
    setNewPOIColor('#007bff');
    setSelectedExistingPOI('');
    setPOIModalStep('choice'); // Reset to choice step
  };

  const handlePOIChoiceCreate = () => {
    setPOIModalStep('create');
  };

  const handlePOIChoiceAssign = () => {
    setPOIModalStep('assign');
  };

  const handleCreatePOI = async () => {
    if (!selectedLocationForPOI || !newPOIName.trim()) {
      showErrorToast('Please fill in all required fields');
      return;
    }

    try {
      const poiData = {
        title: newPOIName.trim(),
        x: selectedLocationForPOI.position?.x || 0,
        y: selectedLocationForPOI.position?.y || 0,
        z: selectedLocationForPOI.position?.z || 0,
        icon: newPOIIcon,
        poi_type_name: newPOIType.trim() || 'Generic',
        poi_group_name: newPOICategory,
        visibility_check: true,
        importance: "5.00",
        custom_data: JSON.stringify({
          description: newPOIDescription.trim(),
          location: selectedLocationForPOI.name,
          location_id: selectedLocationForPOI.id,
          status: 'active',
          created_manually: true
        })
      };

      const response = await apiService.post(`/assets/${resolvedAssetId}/pois/batch`, { pois: [poiData] });

      if (response) {
        setShowPOIModal(false);
        await loadPOIs(); // Reload POIs
        showSuccessToast('Asset created successfully!');
      }
    } catch (error) {
      console.error('Failed to create POI:', error);
      showErrorToast(`Failed to create asset: ${error.message || 'Unknown error'}`);
    }
  };

  const handleAssignExistingPOI = async () => {
    if (!selectedLocationForPOI || !selectedExistingPOI) {
      showErrorToast('Please select an asset to assign');
      return;
    }

    try {
      const poi = existingPOIs.find(p => p.id.toString() === selectedExistingPOI);
      if (!poi) {
        showErrorToast('Selected asset not found');
        return;
      }

      // Update POI with location information
      const customData = parseCustomData(poi) || {};
      const updatedPOIData = {
        ...poi,
        custom_data: JSON.stringify({
          ...customData,
          location: selectedLocationForPOI.name,
          location_id: selectedLocationForPOI.id
        }),
        x: selectedLocationForPOI.position?.x || parseFloat(poi.x) || 0,
        y: selectedLocationForPOI.position?.y || parseFloat(poi.y) || 0,
        z: selectedLocationForPOI.position?.z || parseFloat(poi.z) || 0
      };

      const response = await apiService.put(`/assets/${resolvedAssetId}/pois/${poi.id}`, updatedPOIData);

      if (response) {
        setShowPOIModal(false);
        await loadPOIs(); // Reload POIs
        showSuccessToast('Asset assigned successfully!');
      }
    } catch (error) {
      console.error('Failed to assign POI:', error);
      showErrorToast(`Failed to assign asset: ${error.message || 'Unknown error'}`);
    }
  };

  const handleRemovePOIFromLocation = async (poiId: string | number) => {
    if (!confirm('Are you sure you want to remove this asset from this location?')) {
      return;
    }

    try {
      const poiIdString = poiId.toString();
      const poi = existingPOIs.find(p => p.id.toString() === poiIdString);
      if (!poi) {
        console.error('POI not found:', poiIdString);
        return;
      }

      // Update POI to remove location assignment
      const customData = parseCustomData(poi) || {};
      const updatedPOIData = {
        ...poi,
        custom_data: JSON.stringify({
          ...customData,
          location: '',
          location_id: undefined
        })
      };

      const response = await apiService.put(`/assets/${resolvedAssetId}/pois/${poiIdString}`, updatedPOIData);

      if (response) {
        await loadPOIs(); // Reload POIs
        showSuccessToast('Asset removed from location successfully!');
      }
    } catch (error) {
      console.error('Failed to remove POI:', error);
      showErrorToast(`Failed to remove asset: ${error.message || 'Unknown error'}`);
    }
  };

  // Load POIs when component mounts or resolvedAssetId changes
  useEffect(() => {
    if (resolvedAssetId) {
      loadPOIs();
    }
    // Load once per asset. loadPOIs is re-created every render and sets existingPOIs, so
    // depending on it would re-fetch every POI on its own result.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedAssetId]);

  // Load POIs when assignment step is selected
  useEffect(() => {
    if (poiModalStep === 'assign' && resolvedAssetId) {
      loadPOIs();
    }
    // Same one-shot as above — fires on entering the assign step, not on loadPOIs identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poiModalStep, resolvedAssetId]);

  // Initialize all categories as collapsed when POIs are loaded
  useEffect(() => {
    if (existingPOIs.length > 0) {
      const allSubcategories = new Set(existingPOIs.map(poi => extractPOISubcategory(poi)));
      resetCollapsedCategories(allSubcategories);
    }
  }, [existingPOIs, resetCollapsedCategories]);

  // No longer need to initialize collapsed state - empty set means all collapsed by default

  // Helper function to extract subcategory from POI data
  const extractPOISubcategory = (poi: any): string => {
    // First try the database subcategory field
    if (poi.subcategory && poi.subcategory.trim() !== '') {
      return poi.subcategory;
    }

    // Canonical ObjectType derivation (utils/POIFieldExtractor, E-12c) — the page-local
    // regex disagreed with SystemsPage's, splitting the same POI into different groups.
    const derived = deriveSubcategoryFromObjectType(poi);
    if (derived) return derived;

    // Fallback to poi_type_name
    return poi.poi_type_name || poi.properties?.type || 'Other';
  };

  // Helper function to search property set values in POI custom_data
  const searchPropertySetValues = (poi: any, searchLower: string): boolean => {
    try {
      const customData = parseCustomData(poi);

      if (!customData?.ifc?.propertySets) return false;

      // Search through all property set values
      for (const [, properties] of Object.entries(customData.ifc.propertySets)) {
        if (properties && typeof properties === 'object') {
          for (const [, value] of Object.entries(properties as Record<string, any>)) {
            if (value !== null && value !== undefined) {
              const strValue = String(value).toLowerCase();
              if (strValue === searchLower) {
                return true;
              }
            }
          }
        }
      }
    } catch (e) {
      // Ignore parse errors
    }
    return false;
  };

  // Helper function to check if a POI matches search term
  const poiMatchesSearch = (poi: any, searchLower: string): boolean => {
    // Check basic fields
    if ((poi.title || '').toLowerCase().includes(searchLower)) return true;
    if ((poi.name || '').toLowerCase().includes(searchLower)) return true;
    if ((poi.subcategory || '').toLowerCase().includes(searchLower)) return true;
    if ((poi.poi_type_name || '').toLowerCase().includes(searchLower)) return true;
    // Check property set values
    if (searchPropertySetValues(poi, searchLower)) return true;
    return false;
  };

  // Helper function to get appropriate icon for POI: the canonical keyword table
  // (Categories.getIconForCategory) over subcategory first, then title; then the POI's
  // own stored icon; then its category. The page-local keyword map that lived here
  // disagreed with the canonical on shared concepts (💧/🌬️/🔒/🔥/🧱 vs 🚰/🌡️/🔐/🚨/🏗️),
  // so the same POI showed different icons per page — canonical wins (2026-07-06).
  const getPOIIcon = (poi: any): string => {
    const bySubcategory = getIconForCategory(extractPOISubcategory(poi));
    if (bySubcategory !== '📦') return bySubcategory;

    const byTitle = getIconForCategory(poi.title || poi.name || '');
    if (byTitle !== '📦') return byTitle;

    // Use existing icon if it's not generic
    if (poi.icon && poi.icon !== '📍') return poi.icon;
    if (poi.visualization?.icon && poi.visualization.icon !== '📍') return poi.visualization.icon;

    if (poi.category) return getIconForCategory(poi.category);

    return '📍'; // Default icon
  };

  // Category collapse toggle is provided by useToggleSet (toggleCategoryCollapse)

  // Helper function to toggle space expand/collapse
  // (keeps the two-arg signature its callers use; the optional location was always unused)
  const toggleSpaceCollapse = (locationId: string, location?: Location) => {
    toggleExpandedSpace(locationId);
  };

  // File and POI expand/collapse toggles are provided by useToggleSet
  // (toggleFileCollapse, togglePOIExpand)

  // Helper function to parse custom_data JSON for POI details
  // custom_data parsing: canonical parseCustomData (utils/POIFieldExtractor) — the local
  // copy this page carried was one of 12 re-implementations (three fallback semantics).

  // Helper function to format property values for display
  const formatPropertyValue = (value: any): string => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return value.toFixed(3);
    if (typeof value === 'string') return value;
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  // Category icon for subcategory group headers — the canonical keyword table
  // (was a second page-local map with the same conflicts as getPOIIcon's).
  const getCategoryIcon = (subcategory: string): string => getIconForCategory(subcategory);

  const getTypeIcon = (type: string): string => {
    switch (type.toLowerCase()) {
      case 'building':
      case 'ifcbuilding':
        return '🏢';
      case 'buildingstorey':
      case 'ifcbuildingstorey':
      case 'floor':
        return '↔️';
      case 'building storey':
        return '↔️';
      case 'space':
      case 'ifcspace':
      case 'room':
        return '🚪';
      case 'spaces':
        return '🚪';
      case 'empty spaces group':
        return '📭'; // Empty mailbox icon for empty spaces
      case 'empty storeys group':
        return '📭'; // Empty mailbox icon for empty storeys
      case 'site':
      case 'ifcsite':
        return '🌍';
      case 'project':
      case 'ifcproject':
        return '📋';
      default:
        return '📍';
    }
  };

  // Filter locations based on search term
  const filterLocations = (locs: Location[], term: string): Location[] => {
    if (!term.trim()) return locs;

    const searchTerm = term.toLowerCase();

    return locs.filter(location => {
      // Check if current location matches
      const locationMatches =
        location.name?.toLowerCase().includes(searchTerm) ||
        location.type?.toLowerCase().includes(searchTerm) ||
        location.description?.toLowerCase().includes(searchTerm);

      // Check if any POIs in this location match (including property set values)
      let poiMatches = false;
      const poisByFile = (location as any).poisByFile;
      if (poisByFile) {
        for (const fileName of Object.keys(poisByFile)) {
          const pois = poisByFile[fileName];
          if (Array.isArray(pois)) {
            for (const poi of pois) {
              if (poiMatchesSearch(poi, searchTerm)) {
                poiMatches = true;
                break;
              }
            }
          }
          if (poiMatches) break;
        }
      }

      // Check if any children match
      const childMatches = location.children ?
        filterLocations(location.children, term).length > 0 : false;

      return locationMatches || poiMatches || childMatches;
    }).map(location => ({
      ...location,
      children: location.children ? filterLocations(location.children, term) : []
    }));
  };

  const filteredLocations = filterLocations(locations, searchTerm);

  // Filter unassigned POIs (POIs without location_id) when there's a search term
  const filteredUnassignedPOIs = searchTerm.trim() ? existingPOIs.filter(poi => {
    // Only include POIs without location assignment
    const hasLocation = (poi as any).location_id || parseCustomData(poi)?.location_id;

    if (hasLocation) return false;

    // Check if POI matches search term
    return poiMatchesSearch(poi, searchTerm.toLowerCase());
  }) : [];

  // Count total locations recursively
  const countLocations = (locs: Location[]): number => {
    return locs.reduce((sum, loc) => sum + 1 + (loc.children ? countLocations(loc.children) : 0), 0);
  };
  const totalLocations = countLocations(locations);
  const filteredLocationsCount = countLocations(filteredLocations);

  // Count filtered assets when search is active
  const filteredAssetsCount = searchTerm.trim()
    ? existingPOIs.filter(poi => poiMatchesSearch(poi, searchTerm.toLowerCase())).length
    : existingPOIs.length;

  // Room-assignment coverage over the server tree: a storey node's own poiCount is the
  // number of POIs assigned DIRECTLY to the storey (rooms carry their own counts), so
  // rooms-exist + storey-direct>0 means "Reassign locations" hasn't caught up — a fresh
  // RVT site imports every POI at storey level and room features silently miss them.
  const roomAssignmentGap = useMemo(() => {
    let storeyDirect = 0;
    let roomsExist = false;
    const walk = (nodes: Location[]) => {
      (nodes || []).forEach((n: any) => {
        if (n.type === 'Spaces') roomsExist = true;
        else if (n.type === 'Building Storey') storeyDirect += Number(n.poiCount) || 0;
        if (n.children && n.children.length) walk(n.children);
      });
    };
    walk(locations);
    return { storeyDirect, roomsExist };
  }, [locations]);

  const renderLocation = (location: Location, depth: number = 0) => {
    // Check if this is an empty space (space with 0 POIs/elements)
    const isEmptySpace = location.type === 'Spaces' && (location as any).isEmpty === true;
    const isEmptySpacesGroup = location.type === 'Empty Spaces Group';

    // Check if this is an empty storey (storey with 0 total POIs)
    const isEmptyStorey = location.type === 'Building Storey' && (location as any).isEmpty === true;
    const isEmptyStoreysGroup = location.type === 'Empty Storeys Group';

    const hasChildren = (location.children && location.children.length > 0) ||
                       ((location as any).poisByFile && Object.keys((location as any).poisByFile).length > 0) ||
                       (location.type === 'Spaces' && !isEmptySpace); // Show expand arrow for non-empty spaces
    const isExpanded = expandedSpaces.has(location.id); // Direct logic - empty set = all collapsed

    // Empty spaces and empty storeys should not be clickable to expand
    const isClickable = !isEmptySpace && !isEmptyStorey;

    return (
      <div key={location.id} style={{ marginInlineStart: depth * 20 + 'px' }}>
        <div
          id={`location-${location.id}`}
          className={`location-card`}
          onClick={isClickable ? () => toggleSpaceCollapse(location.id, location) : undefined}
          style={{ cursor: isClickable ? 'pointer' : 'default', ...(editStoreyDropdownId === location.id ? { zIndex: 1002 } : {}) }}
        >
          <div className="location-info">
            {hasChildren && (
              <span style={{
                fontSize: '14px',
                color: colors.textSecondary,
                marginInlineEnd: '8px',
                transition: 'transform 0.2s ease'
              }}>
                {isExpanded ? '▼' : (isRTL ? '◀' : '▶')}
              </span>
            )}

            <span style={{
              fontSize: '20px'
            }}>
              {getTypeIcon(location.type)}
            </span>

            <div>
              <div style={{ fontWeight: 500, color: colors.textPrimary, fontSize: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                {location.name}
                {location.is_custom && (
                  <span style={{
                    fontSize: '10px', padding: '2px 6px',
                    backgroundColor: 'rgba(144, 190, 109, 0.2)', color: '#90BE6D',
                    borderRadius: '4px', fontWeight: 600
                  }}>Custom</span>
                )}
              </div>
              <div style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '2px' }}>
                {location.type}
              </div>
            </div>
          </div>

          <div className="location-actions">
            {!isEmptySpace && !isEmptyStorey && !isEmptySpacesGroup && !isEmptyStoreysGroup && (
              <>
                {onOpenTickets && <button className="dt-btn sm" onClick={e => {
                  e.stopPropagation(); onOpenTickets({ scope_type: 'location', scope_id: String(location.id), name: location.name });
                }}>{t('tickets')}</button>}
                {onCreateTicket && <button className="dt-btn sm" onClick={e => {
                  e.stopPropagation(); onCreateTicket({ scope_type: 'location', scope_id: String(location.id), name: location.name });
                }}>{t('dt_tickets_new')}</button>}
              </>
            )}
            {canEditContent && location.is_custom && location.type === 'Building Storey' && (
                  <div data-edit-storey-dropdown style={{ position: 'relative', display: 'inline-block' }} onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => setEditStoreyDropdownId(editStoreyDropdownId === location.id ? null : location.id)}
                      className="btn btn-secondary btn-small"
                      style={{
                        padding: '4px 8px',
                        fontSize: '12px',
                        marginRight: '4px'
                      }}
                      title="Edit custom storey"
                    >
                      Edit ▾
                    </button>
                    {editStoreyDropdownId === location.id && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        insetInlineEnd: 0,
                        backgroundColor: colors.background,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        zIndex: 1001,
                        minWidth: '200px',
                        overflow: 'hidden'
                      }}>
                        {loadedIfcFiles && loadedIfcFiles.length > 0 && (!loadingIfcFiles || loadingIfcFiles.length === 0) && onEditCustomStorey ? (
                          <button
                            onClick={() => {
                              onEditCustomStorey(location);
                              setEditStoreyDropdownId(null);
                            }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '8px',
                              width: '100%', padding: '8px 12px', border: 'none',
                              background: 'none', cursor: 'pointer', fontSize: '12px',
                              color: colors.textPrimary, textAlign: 'left'
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.backgroundSecondary)}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                          >
                            Interactive (click on model)
                          </button>
                        ) : (onRequestLoadModel || onSwitchToViewer) && (!loadedIfcFiles || loadedIfcFiles.length === 0) ? (
                          <button
                            onClick={() => {
                              if (rvtSourceFile && onRequestLoadModel) {
                                onRequestLoadModel(rvtSourceFile);
                              } else if (onSwitchToViewer) {
                                onSwitchToViewer();
                              }
                              setEditStoreyDropdownId(null);
                            }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '8px',
                              width: '100%', padding: '8px 12px', border: 'none',
                              background: 'none', cursor: 'pointer', fontSize: '12px',
                              color: colors.textPrimary, textAlign: 'left'
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.backgroundSecondary)}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                          >
                            Load Model
                          </button>
                        ) : loadingIfcFiles && loadingIfcFiles.length > 0 ? (
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            padding: '8px 12px', fontSize: '12px', color: colors.textSecondary
                          }}>
                            Loading model...
                          </div>
                        ) : null}
                        <button
                          onClick={() => {
                            setEditingStoreyLocation(location);
                            setManualStoreyName(location.name);
                            setManualStoreyBottom(String(parseFloat(String(location.elevation || 0))));
                            setManualStoreyTop(String(parseFloat(String(location.elevation || 0)) + parseFloat(String(location.height || 0))));
                            setShowManualStoreyModal(true);
                            setEditStoreyDropdownId(null);
                          }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            width: '100%', padding: '8px 12px', border: 'none',
                            background: 'none', cursor: 'pointer', fontSize: '12px',
                            color: colors.textPrimary, textAlign: 'left'
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.backgroundSecondary)}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                        >
                          Manual (enter elevations)
                        </button>
                        <button
                          onClick={() => {
                            setSelectedMergeStoreys(new Set([location.id]));
                            setMergeStoreyName('');
                            setDestroyOriginStoreys(false);
                            setShowMergeStoreyModal(true);
                            setEditStoreyDropdownId(null);
                          }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            width: '100%', padding: '8px 12px', border: 'none',
                            background: 'none', cursor: 'pointer', fontSize: '12px',
                            color: colors.textPrimary, textAlign: 'left'
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.backgroundSecondary)}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                        >
                          Merge Existing Storeys
                        </button>
                      </div>
                    )}
                  </div>
            )}
            {canEditContent && location.type === 'Building Storey' && !isEmptyStorey && (
              loadedIfcFiles && loadedIfcFiles.length > 0 && (!loadingIfcFiles || loadingIfcFiles.length === 0) && onStartCustomRoomDrawing ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onStartCustomRoomDrawing(location.id);
                  }}
                  className="btn btn-primary btn-small"
                  style={{
                    padding: '4px 8px',
                    fontSize: '12px',
                    marginRight: '4px'
                  }}
                  title="Draw a custom room in this storey"
                >
                  + Add Room
                </button>
              ) : (!loadedIfcFiles || loadedIfcFiles.length === 0) && (onRequestLoadModel || onSwitchToViewer) ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const fileToLoad = location.source_file || rvtSourceFile;
                    if (fileToLoad && onRequestLoadModel) {
                      onRequestLoadModel(fileToLoad);
                    } else if (onSwitchToViewer) {
                      onSwitchToViewer();
                    }
                  }}
                  className="btn btn-secondary btn-small"
                  style={{
                    padding: '4px 8px',
                    fontSize: '12px',
                    marginRight: '4px'
                  }}
                  title="Load model to enable room drawing"
                >
                  Load Model
                </button>
              ) : null
            )}
            {location.type === 'Spaces' && onNavigateToViewer && location.ifc_guid && location.source_file && loadedIfcFiles?.includes(location.source_file) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onNavigateToViewer({
                    id: location.id,
                    expressId: location.ifc_id,
                    name: location.name,
                    coordinates: location.position || { x: 0, y: 0, z: 0 },
                    properties: {
                      ifcElementGuid: location.ifc_guid,
                      ifcElementExpressId: location.ifc_id,
                      type: location.type
                    },
                    skipInfoPanel: true // Only highlight, don't open ElementInfoPanel
                  } as any);
                }}
                className="btn btn-primary btn-small"
                style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  marginRight: '4px'
                }}
                title="Highlight space in 3D viewer"
              >
                🏢 3D
              </button>
            )}
            {/* Unified RVT Spaces & Custom Rooms 3D button - requires loaded RVT model for both */}
            {location.type === 'Spaces' && onNavigateToViewer && !location.ifc_guid &&
              loadedIfcFiles?.some(f => f.toLowerCase().endsWith('.rvt')) && (
              location.source_file?.toLowerCase().endsWith('.rvt') || (location.is_custom && location.geometry_polygon)
            ) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const loadedRvtFile = loadedIfcFiles?.find(f => f.toLowerCase().endsWith('.rvt')) || location.source_file;
                  onNavigateToViewer(buildSyntheticRoomPoi({
                    id: location.id,
                    name: location.name,
                    elevation: location.elevation,
                    height: location.height,
                    dbId: location.ifc_id ?? null,
                    sourceFile: loadedRvtFile,
                    isCustom: location.is_custom || false,
                    geometryPolygon: location.geometry_polygon
                  }));
                }}
                className="btn btn-primary btn-small"
                style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  marginRight: '4px'
                }}
                title={location.is_custom ? `Show custom room "${location.name}" in 3D viewer` : "Highlight space in 3D viewer"}
              >
                🏢 3D
              </button>
            )}
            {/* RVT Spaces Load Model button - when model is not loaded */}
            {location.type === 'Spaces' && onRequestLoadModel && !location.ifc_guid &&
              !loadedIfcFiles?.some(f => f.toLowerCase().endsWith('.rvt')) && (
              location.source_file?.toLowerCase().endsWith('.rvt') || (location.is_custom && rvtSourceFile)
            ) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const fileToLoad = location.source_file || rvtSourceFile;
                  if (fileToLoad) onRequestLoadModel(fileToLoad);
                }}
                className="btn btn-secondary btn-small"
                style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  marginRight: '4px'
                }}
                title={`Load ${location.source_file || rvtSourceFile} to 3D viewer`}
              >
                Load Model
              </button>
            )}
            {canEditContent && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddPOI(location);
                  }}
                  className="btn btn-primary btn-small"
                  style={{
                    padding: '4px 8px',
                    fontSize: '12px',
                    marginRight: '4px'
                  }}
                  title="Add asset to this location"
                >
                  📍 Add Asset
                </button>
                {location.is_custom && location.type !== 'Building Storey' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditCustomRoom?.(location);
                    }}
                    className="btn btn-secondary btn-small"
                    style={{
                      padding: '4px 8px',
                      fontSize: '12px',
                      marginRight: '4px'
                    }}
                    title="Edit custom room"
                  >
                    Edit
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteLocation(location.id);
                  }}
                  className="btn btn-danger btn-small"
                  style={{
                    padding: '4px 8px',
                    fontSize: '12px'
                  }}
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </div>

        {isExpanded && hasChildren && (
          <div>
            {location.children!.map(child => renderLocation(child, depth + 1))}
          </div>
        )}

        {/* File Cards with POIs nested under them - rendered after spaces/children */}
        {isExpanded && (location as any).poisByFile && (() => {
          // Use backend's poisByFile field which already groups POIs by source file
          const poisByFile = (location as any).poisByFile;
          const files = Object.keys(poisByFile);

          return files.map((fileName, fileIndex) => {
            // POIs are already grouped by source file from backend
            const poisForFile = poisByFile[fileName];

            // Create unique file ID for collapse state
            const fileId = `${location.id}-${fileName}`;
            const hasFilePOIs = poisForFile && poisForFile.length > 0;
            const isFileExpanded = expandedFiles.has(fileId);

            // Skip files with 0 POIs
            if (!hasFilePOIs) {
              return null;
            }

            return (
              <div key={`file-${fileIndex}`} style={{ marginInlineStart: (depth + 1) * 20 + 'px', marginTop: '6px' }}>
                <div
                  className="location-card"
                  style={{
                    backgroundColor: colors.backgroundSecondary,
                    border: `1px solid ${colors.borderLight}`,
                    cursor: hasFilePOIs ? 'pointer' : 'default'
                  }}
                  onClick={hasFilePOIs ? () => toggleFileCollapse(fileId) : undefined}
                >
                  <div className="location-info">
                    {hasFilePOIs && (
                      <span style={{
                        fontSize: '14px',
                        color: colors.textSecondary,
                        marginRight: '8px',
                        transition: 'transform 0.2s ease'
                      }}>
                        {isFileExpanded ? '▼' : '▶'}
                      </span>
                    )}
                    <span style={{ fontSize: '16px' }}>📁</span>
                    <div>
                      <div style={{ fontWeight: 500, color: colors.textPrimary, fontSize: '14px' }}>
                        {fileName}
                      </div>
                      <div style={{ fontSize: '11px', color: colors.textSecondary, marginTop: '1px' }}>
                        File • {poisForFile.length} Assets
                      </div>
                    </div>
                  </div>
                </div>

                {/* POIs for this specific file - only show when file is expanded */}
                {isFileExpanded && poisForFile.filter(poi => !searchTerm.trim() || poiMatchesSearch(poi, searchTerm.toLowerCase())).map((poi) => {
                  const isPOIExpanded = expandedPOIs.has(poi.id);
                  const customData = parseCustomData(poi) || {};
                  const ifcProps = customData.ifc?.properties || customData.ifc?.ifcProperties || {};
                  const propertySets = customData.ifc?.propertySets || {};

                  return (
                    <div key={`poi-${poi.id}`} style={{ marginInlineStart: (depth + 2) * 20 + 'px', marginTop: '4px' }}>
                      <div
                        className="location-card"
                        onClick={() => togglePOIExpand(poi.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="location-info">
                          <span style={{
                            fontSize: '14px',
                            color: colors.textSecondary,
                            marginRight: '4px',
                            transition: 'transform 0.2s ease'
                          }}>
                            {isPOIExpanded ? '▼' : '▶'}
                          </span>
                          <span style={{ fontSize: '16px' }}>
                            {getPOIIcon(poi)}
                          </span>

                          <div>
                            <div style={{ fontWeight: 400, color: colors.textPrimary, fontSize: '14px' }}>
                              {poi.title || poi.name}
                            </div>
                            <div style={{ fontSize: '10px', color: colors.textSecondary, marginTop: '1px' }}>
                              Asset • {poi.subcategory || poi.poi_type_name || poi.properties?.type || poi.category}
                            </div>
                          </div>
                        </div>

                        <div className="location-actions">
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            {onNavigateToViewer && isPOIIfcLoaded(poi, fileName) && (
                              <button
                                className="btn btn-primary btn-small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onNavigateToViewer(poi);
                                }}
                                style={{
                                  fontSize: '10px',
                                  padding: '2px 6px',
                                  lineHeight: '1.2'
                                }}
                              >
                                3D
                              </button>
                            )}
                            {/* Loading indicator when model is being loaded */}
                            {isPOIIfcLoading(poi, fileName) && (
                              <span
                                style={{
                                  fontSize: '10px',
                                  padding: '2px 6px',
                                  lineHeight: '1.2',
                                  color: colors.textSecondary,
                                  fontStyle: 'italic',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                                title={`Loading ${getPOISourceFile(poi, fileName)}...`}
                              >
                                <span style={{
                                  width: '10px',
                                  height: '10px',
                                  border: '2px solid ' + colors.border,
                                  borderTopColor: colors.primary,
                                  borderRadius: '50%',
                                  animation: 'spin 1s linear infinite',
                                  display: 'inline-block'
                                }} />
                                Loading...
                              </span>
                            )}
                            {/* Load Model button when model is not loaded and not loading */}
                            {onRequestLoadModel && !isPOIIfcLoaded(poi, fileName) && !isPOIIfcLoading(poi, fileName) && getPOISourceFile(poi, fileName) && (
                              <button
                                className="btn btn-secondary btn-small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const sourceFile = getPOISourceFile(poi, fileName);
                                  if (sourceFile) onRequestLoadModel(sourceFile);
                                }}
                                style={{
                                  fontSize: '10px',
                                  padding: '2px 6px',
                                  lineHeight: '1.2'
                                }}
                                title={`Load ${getPOISourceFile(poi, fileName)} to 3D viewer`}
                              >
                                Load Model
                              </button>
                            )}
                            {canEditContent && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemovePOIFromLocation(poi.id);
                                }}
                                className="btn btn-secondary btn-small"
                                style={{
                                  padding: '3px 6px',
                                  fontSize: '10px'
                                }}
                                title="Remove asset from this location"
                              >
                                Unassign
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Expanded POI Details */}
                      {isPOIExpanded && (
                        <div style={{
                          marginTop: '4px',
                          marginInlineStart: '24px',
                          padding: '12px',
                          backgroundColor: colors.backgroundSecondary,
                          borderRadius: '8px',
                          border: `1px solid ${colors.border}`,
                          fontSize: '12px'
                        }}>
                          {/* Classification Section */}
                          <div style={{ marginBottom: '12px' }}>
                            <div style={{
                              fontWeight: 600,
                              color: colors.primary,
                              fontSize: '11px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              marginBottom: '8px',
                              borderBottom: `1px solid ${colors.border}`,
                              paddingBottom: '4px'
                            }}>
                              Classification
                            </div>
                            <div style={{ display: 'grid', gap: '4px' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px' }}>
                                <span style={{ color: colors.textSecondary, fontWeight: 500 }}>Group:</span>
                                <span style={{ color: colors.textPrimary }}>{(poi as any).poi_group_name || 'Unknown'}</span>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px' }}>
                                <span style={{ color: colors.textSecondary, fontWeight: 500 }}>Category:</span>
                                <span style={{ color: colors.textPrimary }}>{(poi as any).ifc_category || poi.category || 'Unknown'}</span>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px' }}>
                                <span style={{ color: colors.textSecondary, fontWeight: 500 }}>Type:</span>
                                <span style={{ color: colors.textPrimary }}>{(poi as any).poi_type_name || poi.properties?.type || 'Unknown'}</span>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px' }}>
                                <span style={{ color: colors.textSecondary, fontWeight: 500 }}>Subcategory:</span>
                                <span style={{ color: colors.textPrimary }}>{poi.subcategory || 'N/A'}</span>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px' }}>
                                <span style={{ color: colors.textSecondary, fontWeight: 500 }}>Importance:</span>
                                <span style={{ color: colors.textPrimary }}>{(poi as any).importance || 'N/A'}</span>
                              </div>
                            </div>
                          </div>

                          {/* Location Context Section */}
                          <div style={{ marginBottom: '12px' }}>
                            <div style={{
                              fontWeight: 600,
                              color: colors.primary,
                              fontSize: '11px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              marginBottom: '8px',
                              borderBottom: `1px solid ${colors.border}`,
                              paddingBottom: '4px'
                            }}>
                              Location Context
                            </div>
                            <div style={{ display: 'grid', gap: '4px' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px' }}>
                                <span style={{ color: colors.textSecondary, fontWeight: 500 }}>Source File:</span>
                                <span style={{ color: colors.textPrimary, fontFamily: 'monospace', fontSize: '10px', wordBreak: 'break-all' }}>
                                  {(poi as any).ifc_source_file || 'Unknown'}
                                </span>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px' }}>
                                <span style={{ color: colors.textSecondary, fontWeight: 500 }}>Coordinate System:</span>
                                <span style={{ color: colors.textPrimary }}>{(poi as any).coordinate_system || 'model_local'}</span>
                              </div>
                              {poi.coordinates && (
                                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px' }}>
                                  <span style={{ color: colors.textSecondary, fontWeight: 500 }}>Position:</span>
                                  <span style={{ color: colors.textPrimary, fontFamily: 'monospace', fontSize: '10px' }}>
                                    ({Number(poi.coordinates.x).toFixed(3)}, {Number(poi.coordinates.y).toFixed(3)}, {poi.coordinates.z ? Number(poi.coordinates.z).toFixed(3) : '0.000'})
                                  </span>
                                </div>
                              )}
                              {(poi as any).location_id && (
                                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px' }}>
                                  <span style={{ color: colors.textSecondary, fontWeight: 500 }}>Location ID:</span>
                                  <span style={{ color: colors.textPrimary, fontFamily: 'monospace', fontSize: '10px' }}>{(poi as any).location_id}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* IFC Element Details Section */}
                          {((poi as any).ifc_element_guid || (poi as any).ifc_element_express_id || poi.properties?.ifcElementGuid) && (
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{
                                fontWeight: 600,
                                color: colors.primary,
                                fontSize: '11px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                marginBottom: '8px',
                                borderBottom: `1px solid ${colors.border}`,
                                paddingBottom: '4px'
                              }}>
                                IFC Element Details
                              </div>
                              <div style={{ display: 'grid', gap: '4px' }}>
                                {((poi as any).ifc_element_express_id || poi.properties?.ifcElementExpressId) && (
                                  <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px' }}>
                                    <span style={{ color: colors.textSecondary, fontWeight: 500 }}>Express ID:</span>
                                    <span style={{ color: colors.textPrimary, fontFamily: 'monospace', fontWeight: 'bold' }}>
                                      {(poi as any).ifc_element_express_id || poi.properties?.ifcElementExpressId}
                                    </span>
                                  </div>
                                )}
                                {((poi as any).ifc_element_guid || poi.properties?.ifcElementGuid) && (
                                  <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px' }}>
                                    <span style={{ color: colors.textSecondary, fontWeight: 500 }}>GUID:</span>
                                    <span style={{ color: colors.textPrimary, fontFamily: 'monospace', fontSize: '9px', wordBreak: 'break-all' }}>
                                      {(poi as any).ifc_element_guid || poi.properties?.ifcElementGuid}
                                    </span>
                                  </div>
                                )}
                                {(poi as any).ifc_type_name && (
                                  <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px' }}>
                                    <span style={{ color: colors.textSecondary, fontWeight: 500 }}>IFC Type:</span>
                                    <span style={{ color: colors.textPrimary }}>{(poi as any).ifc_type_name}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Property Sets Section (from custom_data.ifc.propertySets) */}
                          {Object.keys(propertySets).length > 0 && (
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{
                                fontWeight: 600,
                                color: colors.primary,
                                fontSize: '11px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                marginBottom: '8px',
                                borderBottom: `1px solid ${colors.border}`,
                                paddingBottom: '4px'
                              }}>
                                Property Sets ({Object.keys(propertySets).length})
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {Object.entries(propertySets).slice(0, 5).map(([setName, properties]) => (
                                  <div
                                    key={setName}
                                    style={{
                                      backgroundColor: colors.surface,
                                      borderRadius: '4px',
                                      padding: '8px',
                                      border: `1px solid ${colors.borderLight}`
                                    }}
                                  >
                                    <div style={{
                                      fontWeight: 600,
                                      color: colors.primary,
                                      marginBottom: '6px',
                                      fontSize: '11px'
                                    }}>
                                      {setName}
                                    </div>
                                    <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
                                      {Object.entries(properties as Record<string, any>).slice(0, 8).map(([key, value]) => (
                                        <div key={key} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', marginBottom: '2px' }}>
                                          <span style={{ color: colors.textSecondary, fontSize: '10px' }}>{key}:</span>
                                          <span style={{ color: colors.textPrimary, fontSize: '10px', fontFamily: 'monospace' }}>
                                            {formatPropertyValue(value)}
                                          </span>
                                        </div>
                                      ))}
                                      {Object.keys(properties as object).length > 8 && (
                                        <div style={{ color: colors.textSecondary, fontSize: '9px', fontStyle: 'italic' }}>
                                          ... and {Object.keys(properties as object).length - 8} more
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                                {Object.keys(propertySets).length > 5 && (
                                  <div style={{ color: colors.textSecondary, fontSize: '10px', fontStyle: 'italic' }}>
                                    ... and {Object.keys(propertySets).length - 5} more property sets
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Extended IFC Properties Section (from custom_data.ifc.properties) */}
                          {Object.keys(ifcProps).length > 0 && (
                            <div>
                              <div style={{
                                fontWeight: 600,
                                color: colors.primary,
                                fontSize: '11px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                marginBottom: '8px',
                                borderBottom: `1px solid ${colors.border}`,
                                paddingBottom: '4px'
                              }}>
                                Extended Properties ({Object.keys(ifcProps).length})
                              </div>
                              <div style={{
                                backgroundColor: colors.surface,
                                borderRadius: '4px',
                                padding: '8px',
                                maxHeight: '120px',
                                overflowY: 'auto'
                              }}>
                                {Object.entries(ifcProps).slice(0, 15).map(([key, value]) => (
                                  <div key={key} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', marginBottom: '2px' }}>
                                    <span style={{ color: colors.textSecondary, fontSize: '10px' }}>{key}:</span>
                                    <span style={{ color: colors.textPrimary, fontSize: '10px', fontFamily: 'monospace' }}>
                                      {formatPropertyValue(value)}
                                    </span>
                                  </div>
                                ))}
                                {Object.keys(ifcProps).length > 15 && (
                                  <div style={{ color: colors.textSecondary, fontSize: '9px', fontStyle: 'italic', marginTop: '4px' }}>
                                    ... and {Object.keys(ifcProps).length - 15} more properties
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          });
        })()}
      </div>
    );
  };

  if (loading) {
    return (
      <div dir={isRTL ? 'rtl' : 'ltr'} style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        flexDirection: 'column'
      }}>
        <div className="loading-spinner"></div>
        <p style={{ marginTop: '16px', color: colors.textSecondary }}>{t('loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div dir={isRTL ? 'rtl' : 'ltr'} style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        padding: '40px'
      }}>
        <div style={{
          backgroundColor: colors.backgroundSecondary,
          borderRadius: '12px',
          padding: '40px',
          textAlign: 'center',
          maxWidth: '400px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📍</div>
          <h3 style={{ color: colors.textPrimary, marginBottom: '12px' }}>{t('locations')}</h3>
          <p style={{ color: colors.textSecondary, marginBottom: '24px' }}>{error}</p>
          <button
            className="btn btn-primary"
            onClick={() => loadLocations()}
          >
            {t('retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="locations-page" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="locations-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <h1 style={{ margin: 0 }}>📍 {t('locations')}</h1>
          <span style={{ fontSize: '14px', color: colors.textSecondary }}>
            {filteredLocationsCount} of {totalLocations} locations | {filteredAssetsCount} of {existingPOIs.length} assets
            {searchTerm.trim() && ` matching "${searchTerm}"`}
          </span>
        </div>
        <div className="header-actions">
          <input
            type="text"
            placeholder={t('searchLocations')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button
            onClick={exportRoomsCSV}
            className="btn btn-secondary"
            disabled={!locations || locations.length === 0}
            title="Export all rooms to CSV"
          >
            Export Rooms CSV
          </button>
          {/* Admin users always have write access; others need explicit write permission */}
          {canEditContent && (
            <>
              <button
                onClick={handleOpenSetLocationModal}
                className="btn btn-secondary"
                title="Configure property-based location assignment for all assets"
              >
                Assign Locations
              </button>
              {rvtSourceFile && (
              <div ref={createLocationDropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
                <button
                  onClick={() => setShowCreateLocationDropdown(!showCreateLocationDropdown)}
                  className="btn btn-primary"
                  style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  {t('createLocation')}
                  <span style={{ fontSize: '10px', marginLeft: '2px' }}>{showCreateLocationDropdown ? '\u25B2' : '\u25BC'}</span>
                </button>
                {showCreateLocationDropdown && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    insetInlineStart: 0,
                    marginTop: '4px',
                    minWidth: '200px',
                    backgroundColor: colors.background,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    overflow: 'visible'
                  }}>
                    {/* Load Model - shown only when no model is loaded */}
                    {(!loadedIfcFiles || loadedIfcFiles.length === 0) && (onRequestLoadModel || onSwitchToViewer) && (
                      <button
                        onClick={() => {
                          if (rvtSourceFile && onRequestLoadModel) {
                            onRequestLoadModel(rvtSourceFile);
                          } else if (onSwitchToViewer) {
                            onSwitchToViewer();
                          }
                        }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '8px',
                          width: '100%', padding: '10px 14px', border: 'none',
                          background: 'none', cursor: 'pointer', fontSize: '13px',
                          color: colors.textPrimary, textAlign: 'left'
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.backgroundSecondary)}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                      >
                        <span style={{ color: colors.primary }}>&#x1F4E6;</span>
                        Load Model
                      </button>
                    )}
                    {/* Loading indicator - shown while model is loading */}
                    {loadingIfcFiles && loadingIfcFiles.length > 0 && (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '10px 14px', fontSize: '13px', color: colors.textSecondary
                      }}>
                        <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>&#x23F3;</span>
                        Loading model...
                      </div>
                    )}
                    {/* Draw Custom Room - shown only when model is fully loaded */}
                    {loadedIfcFiles && loadedIfcFiles.length > 0 && (!loadingIfcFiles || loadingIfcFiles.length === 0) && onStartCustomRoomDrawing && (
                      <button
                        onClick={() => {
                          onStartCustomRoomDrawing();
                          setShowCreateLocationDropdown(false);
                        }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '8px',
                          width: '100%', padding: '10px 14px', border: 'none',
                          background: 'none', cursor: 'pointer', fontSize: '13px',
                          color: colors.textPrimary, textAlign: 'left'
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.backgroundSecondary)}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                      >
                        <span style={{ color: '#90BE6D' }}>&#x270F;&#xFE0F;</span>
                        Draw Custom Room
                      </button>
                    )}
                    {/* Create Custom Storey - sub-menu with 3 options, only when model loaded */}
                    {loadedIfcFiles && loadedIfcFiles.length > 0 && (!loadingIfcFiles || loadingIfcFiles.length === 0) && (
                    <div
                      style={{ position: 'relative' }}
                      onMouseEnter={() => setShowStoreySubMenu(true)}
                      onMouseLeave={() => setShowStoreySubMenu(false)}
                    >
                      <button
                        style={{
                          display: 'flex', alignItems: 'center', gap: '8px',
                          width: '100%', padding: '10px 14px', border: 'none',
                          background: 'none', cursor: 'pointer', fontSize: '13px',
                          color: colors.textPrimary, textAlign: 'left',
                          justifyContent: 'space-between'
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.backgroundSecondary)}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                        onClick={() => setShowStoreySubMenu(!showStoreySubMenu)}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ color: '#48CAE4' }}>&#x1F3D7;&#xFE0F;</span>
                          Create Custom Storey
                        </span>
                        <span style={{ fontSize: '10px', color: colors.textSecondary }}>{isRTL ? '◂' : '▸'}</span>
                      </button>
                      {showStoreySubMenu && (
                        <div style={{
                          position: 'absolute', ...(isRTL ? { right: '100%' } : { left: '100%' }), top: 0,
                          backgroundColor: colors.background,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          zIndex: 1001, minWidth: '200px', overflow: 'hidden'
                        }}>
                          {loadedIfcFiles && loadedIfcFiles.length > 0 && (!loadingIfcFiles || loadingIfcFiles.length === 0) && onStartCustomStoreyDrawing && (
                            <button
                              onClick={() => {
                                onStartCustomStoreyDrawing();
                                setShowCreateLocationDropdown(false);
                                setShowStoreySubMenu(false);
                              }}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '8px',
                                width: '100%', padding: '10px 14px', border: 'none',
                                background: 'none', cursor: 'pointer', fontSize: '13px',
                                color: colors.textPrimary, textAlign: 'left'
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.backgroundSecondary)}
                              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                            >
                              Interactive (click on model)
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setShowManualStoreyModal(true);
                              setShowCreateLocationDropdown(false);
                              setShowStoreySubMenu(false);
                            }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '8px',
                              width: '100%', padding: '10px 14px', border: 'none',
                              background: 'none', cursor: 'pointer', fontSize: '13px',
                              color: colors.textPrimary, textAlign: 'left'
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.backgroundSecondary)}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                          >
                            Manual (enter elevations)
                          </button>
                          {locations.filter(l => l.type === 'Building Storey').length >= 2 && (
                          <button
                            onClick={() => {
                              setShowMergeStoreyModal(true);
                              setSelectedMergeStoreys(new Set());
                              setMergeStoreyName('');
                              setDestroyOriginStoreys(false);
                              setShowCreateLocationDropdown(false);
                              setShowStoreySubMenu(false);
                            }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '8px',
                              width: '100%', padding: '10px 14px', border: 'none',
                              background: 'none', cursor: 'pointer', fontSize: '13px',
                              color: colors.textPrimary, textAlign: 'left'
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.backgroundSecondary)}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                          >
                            Merge Existing Storeys
                          </button>
                          )}
                        </div>
                      )}
                    </div>
                    )}
                  </div>
                )}
              </div>
              )}
              {/* Only when there is something to delete, and never as the page's loudest
                  control: this was a solid red button beside two outlined ones, visible with
                  zero locations — it read as the thing the page is for. */}
              {locations && locations.length > 0 && (
                <button
                  onClick={handleDeleteAllLocations}
                  className="btn btn-secondary btn-danger-outline"
                >
                  {t('deleteAll')}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Room-assignment coverage warning: rooms exist but POIs still sit on storeys —
          the state a fresh RVT site is in until "Assign Locations" (reassign) is run */}
      {roomAssignmentGap.roomsExist && roomAssignmentGap.storeyDirect > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          margin: '0 0 12px 0',
          padding: '8px 14px',
          backgroundColor: `${colors.warning}20`,
          border: `1px solid ${colors.warning}`,
          borderRadius: '6px',
          fontSize: '13px',
          color: colors.textPrimary
        }}>
          <span>
            ⚠️ {roomAssignmentGap.storeyDirect} {t('poiRoomGapWarning', 'assets are assigned to storeys but not to rooms — room highlight, filters and counts won\'t cover them.')}
          </span>
          {canEditContent && (
            <button
              className="btn btn-secondary btn-small"
              onClick={handleOpenSetLocationModal}
              title="Configure property-based location assignment for all assets"
            >
              {t('assignLocations', 'Assign Locations')}
            </button>
          )}
        </div>
      )}

      <div className="locations-content">
        {filteredLocations.length === 0 && filteredUnassignedPOIs.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: colors.textSecondary
          }}>
            <p style={{ fontSize: '16px', marginBottom: '8px' }}>
              {searchTerm.trim() ? 'No locations match your search' : 'No locations found'}
            </p>
            {!searchTerm.trim() && (
              <p style={{ fontSize: '14px' }}>
                {t('noLocationsHint', 'Locations are added when you import assets from a model in the Viewer.')}
              </p>
            )}
          </div>
        ) : (
          <div>
            {filteredLocations.map(location => renderLocation(location, 0))}

            {/* Show unassigned POIs that match search */}
            {filteredUnassignedPOIs.length > 0 && (
              <div style={{
                marginTop: filteredLocations.length > 0 ? '16px' : '0',
                borderTop: filteredLocations.length > 0 ? `1px solid ${colors.border}` : 'none',
                paddingTop: filteredLocations.length > 0 ? '16px' : '0'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  backgroundColor: colors.backgroundTertiary,
                  borderRadius: '6px',
                  marginBottom: '8px'
                }}>
                  <span>📦</span>
                  <span style={{ fontWeight: 500, color: colors.textPrimary }}>
                    Unassigned Elements ({filteredUnassignedPOIs.length})
                  </span>
                </div>
                <div style={{ paddingLeft: '24px' }}>
                  {filteredUnassignedPOIs.slice(0, 50).map((poi: any) => (
                    <div
                      key={poi.id}
                      className="poi-item"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginBottom: '2px',
                        backgroundColor: 'transparent',
                        transition: 'background-color 0.15s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = colors.backgroundTertiary;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                      onClick={() => {
                        // Standard navigation path (the previous onSelectElement reference
                        // was an undeclared name — clicking this row threw a ReferenceError)
                        if (poi.properties?.ifcElementExpressId && onNavigateToViewer) {
                          onNavigateToViewer(poi as any);
                        }
                      }}
                    >
                      <span>{getPOIIcon(poi)}</span>
                      <span style={{ flex: 1, fontSize: '13px', color: colors.textPrimary }}>
                        {poi.title || poi.name}
                      </span>
                      <span style={{ fontSize: '11px', color: colors.textMuted }}>
                        {poi.subcategory || poi.properties?.subCategory || ''}
                      </span>
                    </div>
                  ))}
                  {filteredUnassignedPOIs.length > 50 && (
                    <div style={{ padding: '8px 12px', color: colors.textMuted, fontSize: '12px' }}>
                      ... and {filteredUnassignedPOIs.length - 50} more elements
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>


      {/* Create Location Modal */}
      {showCreateLocationModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '500px' }}>
            <div className="modal-content">
              <h3>Create New Location</h3>
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 500,
                  color: colors.textPrimary
                }}>
                  Location Name:
                </label>
                <input
                  type="text"
                  value={newLocationName}
                  onChange={(e) => setNewLocationName(e.target.value)}
                  placeholder="Enter location name"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  autoFocus
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 500,
                  color: colors.textPrimary
                }}>
                  Description (optional):
                </label>
                <textarea
                  value={newLocationDescription}
                  onChange={(e) => setNewLocationDescription(e.target.value)}
                  placeholder="Enter location description"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '4px',
                    fontSize: '14px',
                    minHeight: '80px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div className="modal-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCreateLocationModal(false);
                    setNewLocationName('');
                    setNewLocationDescription('');
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleCreateLocation}
                  disabled={!newLocationName.trim()}
                >
                  Create Location
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* POI Modal */}
      {showPOIModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '600px' }}>
            <div className="modal-content">
              <h3>
                {poiModalStep === 'choice' && 'Add Asset'}
                {poiModalStep === 'create' && 'Create New Asset'}
                {poiModalStep === 'assign' && 'Assign Existing Asset'}
                {selectedLocationForPOI && ` for ${selectedLocationForPOI.name}`}
              </h3>

              {poiModalStep === 'choice' ? (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <p style={{ color: colors.textSecondary, marginBottom: '16px' }}>
                      Choose how you want to add an asset to this location:
                    </p>

                    <button
                      className="btn btn-primary"
                      onClick={handlePOIChoiceCreate}
                      style={{
                        width: '100%',
                        marginBottom: '12px',
                        padding: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      ✨ Create New Asset
                    </button>

                    <button
                      className="btn btn-secondary"
                      onClick={handlePOIChoiceAssign}
                      style={{
                        width: '100%',
                        padding: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      📍 Assign Existing Asset
                    </button>
                  </div>
                </>
              ) : poiModalStep === 'create' ? (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                      Asset Name *
                    </label>
                    <input
                      type="text"
                      value={newPOIName}
                      onChange={(e) => setNewPOIName(e.target.value)}
                      placeholder="Enter asset name"
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: `1px solid ${colors.border}`,
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                      Description
                    </label>
                    <textarea
                      value={newPOIDescription}
                      onChange={(e) => setNewPOIDescription(e.target.value)}
                      placeholder="Enter asset description"
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: `1px solid ${colors.border}`,
                        borderRadius: '4px',
                        fontSize: '14px',
                        minHeight: '60px'
                      }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                        Category
                      </label>
                      <select
                        value={newPOICategory}
                        onChange={(e) => setNewPOICategory(e.target.value as POICategory)}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: `1px solid ${colors.border}`,
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      >
                        {Object.values(POICategory).map(category => (
                          <option key={category} value={category}>
                            {category}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                        Type
                      </label>
                      <input
                        type="text"
                        value={newPOIType}
                        onChange={(e) => setNewPOIType(e.target.value)}
                        placeholder="e.g., Equipment, Furniture"
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: `1px solid ${colors.border}`,
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                        Icon
                      </label>
                      <input
                        type="text"
                        value={newPOIIcon}
                        onChange={(e) => setNewPOIIcon(e.target.value)}
                        placeholder="📍"
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: `1px solid ${colors.border}`,
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                        Color
                      </label>
                      <input
                        type="color"
                        value={newPOIColor}
                        onChange={(e) => setNewPOIColor(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '4px',
                          border: `1px solid ${colors.border}`,
                          borderRadius: '4px',
                          height: '36px'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: colors.backgroundSecondary, borderRadius: '4px' }}>
                    <div style={{ fontSize: '12px', color: colors.textSecondary, marginBottom: '8px' }}>
                      Preview:
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '16px' }}>{newPOIIcon}</span>
                      <span style={{ color: newPOIColor, fontWeight: '500' }}>
                        {newPOIName || 'Asset Name'}
                      </span>
                      <span style={{ color: colors.textSecondary, fontSize: '12px' }}>
                        ({newPOIType || 'Type'})
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                      Select asset to assign *
                    </label>

                    {isLoadingPOIs ? (
                      <div style={{
                        padding: '16px',
                        textAlign: 'center',
                        color: colors.textSecondary,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '4px'
                      }}>
                        Loading assets...
                      </div>
                    ) : (
                      // Group POIs by subcategory and display as organized sections
                      (() => {
                        const grouped = existingPOIs.reduce((acc, poi) => {
                          const subcategory = extractPOISubcategory(poi);
                          if (!acc[subcategory]) acc[subcategory] = [];
                          acc[subcategory].push(poi);
                          return acc;
                        }, {});

                        return (
                          <div style={{
                            maxHeight: '300px',
                            overflowY: 'auto',
                            border: `1px solid ${colors.border}`,
                            borderRadius: '4px',
                            backgroundColor: colors.backgroundPrimary
                          }}>
                            {Object.keys(grouped).sort().map(subcategory => {
                              const isCollapsed = collapsedCategories.has(subcategory);
                              return (
                                <div key={subcategory} style={{ marginBottom: '8px' }}>
                                  {/* Collapsible Subcategory Header */}
                                  <div
                                    onClick={() => toggleCategoryCollapse(subcategory)}
                                    style={{
                                      padding: '8px 12px',
                                      backgroundColor: colors.backgroundSecondary,
                                      borderBottom: `1px solid ${colors.border}`,
                                      fontWeight: '600',
                                      fontSize: '12px',
                                      color: colors.textSecondary,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      cursor: 'pointer',
                                      userSelect: 'none',
                                      transition: 'all 0.2s ease'
                                    }}
                                  >
                                    <span style={{
                                      fontSize: '10px',
                                      transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                                      transition: 'transform 0.2s ease'
                                    }}>
                                      ▼
                                    </span>
                                    {getCategoryIcon(subcategory)} {subcategory} ({grouped[subcategory].length})
                                  </div>

                                  {/* Collapsible POI Cards */}
                                  {!isCollapsed && (
                                    <div style={{
                                      padding: '4px',
                                      maxHeight: '200px',
                                      overflowY: 'auto'
                                    }}>
                                      {grouped[subcategory].map(poi => (
                                        <div
                                          key={poi.id}
                                          onClick={() => setSelectedExistingPOI(poi.id.toString())}
                                          style={{
                                            padding: '8px 12px',
                                            margin: '2px 0',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            border: selectedExistingPOI === poi.id.toString()
                                              ? `2px solid ${colors.primary}`
                                              : `1px solid ${colors.border}`,
                                            backgroundColor: selectedExistingPOI === poi.id.toString()
                                              ? colors.primary + '10'
                                              : colors.backgroundPrimary,
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                          }}
                                        >
                                          <span style={{ fontSize: '14px' }}>{getPOIIcon(poi)}</span>
                                          <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{
                                              fontWeight: '500',
                                              color: colors.textPrimary,
                                              fontSize: '13px',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap'
                                            }}>
                                              {poi.title || poi.name}
                                            </div>
                                            {poi.custom_data && (
                                              <div style={{
                                                fontSize: '11px',
                                                color: colors.textSecondary,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                              }}>
                                                {parseCustomData(poi)?.description || ''}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()
                    )}
                  </div>

                  {selectedExistingPOI && (
                    <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: colors.backgroundSecondary, borderRadius: '4px' }}>
                      {(() => {
                        const poi = existingPOIs.find(p => p.id.toString() === selectedExistingPOI);
                        return poi ? (
                          <div>
                            <div style={{ fontSize: '12px', color: colors.textSecondary, marginBottom: '8px' }}>
                              Selected Asset:
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '16px' }}>{getPOIIcon(poi)}</span>
                              <span style={{ color: colors.textPrimary, fontWeight: '500' }}>
                                {poi.title || poi.name}
                              </span>
                              <span style={{ color: colors.textSecondary, fontSize: '12px' }}>
                                Asset • {extractPOISubcategory(poi)}
                              </span>
                            </div>
                            {poi.custom_data && (
                              <div style={{ marginTop: '4px', fontSize: '12px', color: colors.textSecondary }}>
                                {/* parseCustomData never throws — the raw JSON.parse that
                                    lived here crashed the render on malformed/object custom_data */}
                                {parseCustomData(poi)?.description || poi.title || poi.name}
                              </div>
                            )}
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}

                  {existingPOIs.length === 0 && (
                    <div style={{
                      padding: '16px',
                      backgroundColor: colors.warning + '20',
                      border: `1px solid ${colors.warning}`,
                      borderRadius: '4px',
                      marginBottom: '16px'
                    }}>
                      <div style={{ color: colors.warning, fontSize: '14px', fontWeight: '500' }}>
                        No unassigned assets available
                      </div>
                      <div style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '4px' }}>
                        All assets are already assigned to locations. Create a new asset instead.
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="modal-actions" style={{ gap: '8px' }}>
                {poiModalStep === 'choice' ? (
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowPOIModal(false);
                      resetPOIForm();
                      setPOIModalStep('choice');
                    }}
                  >
                    Cancel
                  </button>
                ) : (
                  <>
                    <button
                      className="btn btn-secondary"
                      onClick={() => setPOIModalStep('choice')}
                    >
                      ← Back
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={poiModalStep === 'create' ? handleCreatePOI : handleAssignExistingPOI}
                      disabled={
                        poiModalStep === 'create'
                          ? !newPOIName.trim()
                          : !selectedExistingPOI
                      }
                    >
                      {poiModalStep === 'create' ? 'Create Asset' : 'Assign Asset'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Set Location Modal */}
      {showSetLocationModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '500px' }}>
            <div className="modal-content">
              <h3>Set Location</h3>
              <p style={{ color: colors.textSecondary, marginBottom: '16px', fontSize: '14px' }}>
                Assign assets to locations using spatial position or property values.
              </p>

              {/* Geolocation Assignment Section */}
              <div style={{
                padding: '16px',
                backgroundColor: colors.backgroundSecondary,
                borderRadius: '8px',
                marginBottom: '20px',
                border: `1px solid ${colors.border}`
              }}>
                {/* Model Loaded Status Indicator */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                  marginBottom: '12px',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  backgroundColor: loadedIfcFiles && loadedIfcFiles.length > 0 ? colors.success + '15' : colors.warning + '15',
                  border: `1px solid ${loadedIfcFiles && loadedIfcFiles.length > 0 ? colors.success : colors.warning}`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: loadedIfcFiles && loadedIfcFiles.length > 0 ? colors.success : colors.warning
                    }} />
                    <span style={{ fontSize: '13px', color: colors.textSecondary }}>
                      {loadedIfcFiles && loadedIfcFiles.length > 0
                        ? `Model Loaded: ${loadedIfcFiles.join(', ')}`
                        : 'No model loaded'
                      }
                    </span>
                  </div>
                  {(!loadedIfcFiles || loadedIfcFiles.length === 0) && onSwitchToViewer && (
                    <button
                      className="btn btn-primary btn-small"
                      onClick={onSwitchToViewer}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      Load Viewer
                    </button>
                  )}
                </div>

                <div style={{ fontWeight: 600, marginBottom: '8px', color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '18px' }}>&#128205;</span>
                  Set by Geolocation
                </div>
                <p style={{ color: colors.textSecondary, fontSize: '13px', marginBottom: '12px' }}>
                  Automatically assign assets to spaces based on their 3D coordinates.
                  Assets inside multiple spaces will be assigned to all of them.
                  Assets not in any space will be assigned to their storey.
                </p>
                <button
                  className="btn btn-primary"
                  onClick={handleAssignByGeolocation}
                  disabled={isAssigningByGeolocation}
                  style={{ width: '100%' }}
                >
                  {isAssigningByGeolocation ? 'Processing...' : 'Assign by Geolocation'}
                </button>

                {geolocationResult && (
                  <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    backgroundColor: geolocationResult.assigned > 0 ? colors.success + '20' : colors.backgroundPrimary,
                    border: `1px solid ${geolocationResult.assigned > 0 ? colors.success : colors.border}`,
                    borderRadius: '4px'
                  }}>
                    <div style={{ fontWeight: 500, marginBottom: '8px', color: colors.textPrimary }}>
                      Geolocation Assignment Complete
                    </div>
                    <div style={{ fontSize: '13px', color: colors.textSecondary }}>
                      <div>Assigned: <strong style={{ color: colors.success }}>{geolocationResult.assigned}</strong></div>
                      <div>In multiple spaces: <strong>{geolocationResult.multipleSpaces}</strong></div>
                      <div>Storey fallback: <strong>{geolocationResult.storeyFallback}</strong></div>
                      {geolocationResult.unassigned > 0 && (
                        <div style={{ color: colors.warning }}>Unassigned: <strong>{geolocationResult.unassigned}</strong></div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '20px',
                color: colors.textSecondary
              }}>
                <div style={{ flex: 1, height: '1px', backgroundColor: colors.border }} />
                <span style={{ padding: '0 12px', fontSize: '12px', textTransform: 'uppercase' }}>or use properties</span>
                <div style={{ flex: 1, height: '1px', backgroundColor: colors.border }} />
              </div>

              {isLoadingProperties ? (
                <div style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: colors.textSecondary
                }}>
                  Loading available properties...
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: 500,
                      color: colors.textPrimary
                    }}>
                      Storey Property:
                    </label>
                    <input
                      type="text"
                      placeholder="Search properties..."
                      value={storeyPropertySearch}
                      onChange={(e) => setStoreyPropertySearch(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '6px 12px',
                        border: `1px solid ${colors.border}`,
                        borderRadius: '4px 4px 0 0',
                        fontSize: '13px',
                        backgroundColor: colors.backgroundSecondary,
                        color: colors.textPrimary,
                        boxSizing: 'border-box'
                      }}
                    />
                    <div
                      style={{
                        width: '100%',
                        maxHeight: '150px',
                        overflowY: 'auto',
                        border: `1px solid ${colors.border}`,
                        borderTop: 'none',
                        borderRadius: '0 0 4px 4px',
                        backgroundColor: colors.backgroundPrimary
                      }}
                    >
                      <div
                        onClick={() => setSelectedStoreyProperty('')}
                        style={{
                          padding: '6px 12px',
                          cursor: 'pointer',
                          backgroundColor: selectedStoreyProperty === '' ? colors.primary + '20' : 'transparent',
                          color: colors.textPrimary,
                          fontSize: '13px'
                        }}
                      >
                        -- None --
                      </div>
                      {availableProperties
                        .filter(prop => prop.toLowerCase().includes(storeyPropertySearch.toLowerCase()))
                        .map(prop => (
                          <div
                            key={prop}
                            onClick={() => setSelectedStoreyProperty(prop)}
                            style={{
                              padding: '6px 12px',
                              cursor: 'pointer',
                              backgroundColor: selectedStoreyProperty === prop ? colors.primary + '20' : 'transparent',
                              color: colors.textPrimary,
                              fontSize: '13px'
                            }}
                          >
                            {prop}
                          </div>
                        ))}
                    </div>
                    <div style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '4px' }}>
                      Property containing storey name (e.g., "G", "1", "B1")
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: 500,
                      color: colors.textPrimary
                    }}>
                      Space Property (optional):
                    </label>
                    <input
                      type="text"
                      placeholder="Search properties..."
                      value={spacePropertySearch}
                      onChange={(e) => setSpacePropertySearch(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '6px 12px',
                        border: `1px solid ${colors.border}`,
                        borderRadius: '4px 4px 0 0',
                        fontSize: '13px',
                        backgroundColor: colors.backgroundSecondary,
                        color: colors.textPrimary,
                        boxSizing: 'border-box'
                      }}
                    />
                    <div
                      style={{
                        width: '100%',
                        maxHeight: '150px',
                        overflowY: 'auto',
                        border: `1px solid ${colors.border}`,
                        borderTop: 'none',
                        borderRadius: '0 0 4px 4px',
                        backgroundColor: colors.backgroundPrimary
                      }}
                    >
                      <div
                        onClick={() => setSelectedSpaceProperty('')}
                        style={{
                          padding: '6px 12px',
                          cursor: 'pointer',
                          backgroundColor: selectedSpaceProperty === '' ? colors.primary + '20' : 'transparent',
                          color: colors.textPrimary,
                          fontSize: '13px'
                        }}
                      >
                        -- None --
                      </div>
                      {availableProperties
                        .filter(prop => prop.toLowerCase().includes(spacePropertySearch.toLowerCase()))
                        .map(prop => (
                          <div
                            key={prop}
                            onClick={() => setSelectedSpaceProperty(prop)}
                            style={{
                              padding: '6px 12px',
                              cursor: 'pointer',
                              backgroundColor: selectedSpaceProperty === prop ? colors.primary + '20' : 'transparent',
                              color: colors.textPrimary,
                              fontSize: '13px'
                            }}
                          >
                            {prop}
                          </div>
                        ))}
                    </div>
                    <div style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '4px' }}>
                      Property containing space/room code (e.g., "GW-G-555")
                    </div>
                  </div>

                  {availableProperties.length === 0 && (
                    <div style={{
                      padding: '12px',
                      backgroundColor: colors.warning + '20',
                      border: `1px solid ${colors.warning}`,
                      borderRadius: '4px',
                      marginBottom: '16px'
                    }}>
                      <div style={{ color: colors.warning, fontSize: '14px' }}>
                        No properties found in asset data.
                      </div>
                    </div>
                  )}

                  {reassignResult && (
                    <div style={{
                      padding: '12px',
                      backgroundColor: reassignResult.updated > 0 ? colors.success + '20' : colors.backgroundSecondary,
                      border: `1px solid ${reassignResult.updated > 0 ? colors.success : colors.border}`,
                      borderRadius: '4px',
                      marginBottom: '16px'
                    }}>
                      <div style={{ fontWeight: 500, marginBottom: '8px', color: colors.textPrimary }}>
                        Reassignment Complete
                      </div>
                      <div style={{ fontSize: '14px', color: colors.textSecondary }}>
                        <div>Updated: <strong>{reassignResult.updated}</strong></div>
                        <div>Unchanged: <strong>{reassignResult.unchanged}</strong></div>
                        {reassignResult.failed > 0 && (
                          <div style={{ color: colors.error }}>Failed: <strong>{reassignResult.failed}</strong></div>
                        )}
                        <div style={{ marginTop: '4px' }}>Total assets: {reassignResult.total}</div>
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="modal-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowSetLocationModal(false);
                    setReassignResult(null);
                    setGeolocationResult(null);
                  }}
                >
                  {(reassignResult || geolocationResult) ? 'Close' : 'Cancel'}
                </button>
                {!reassignResult && (
                  <button
                    className="btn btn-primary"
                    onClick={handleReassignLocations}
                    disabled={isReassigning || (!selectedStoreyProperty && !selectedSpaceProperty)}
                  >
                    {isReassigning ? 'Applying...' : 'Apply'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Manual Storey Creation Modal */}
      {showManualStoreyModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 'var(--dt-z-modal)',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => { setShowManualStoreyModal(false); setEditingStoreyLocation(null); }}>
          <div style={{
            backgroundColor: colors.background, borderRadius: '12px',
            padding: '24px', minWidth: '360px', maxWidth: '420px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)', border: `1px solid ${colors.border}`
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px 0', color: colors.textPrimary, fontSize: '16px' }}>
              {editingStoreyLocation ? 'Edit Storey' : 'Create Storey (Manual)'}
            </h3>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ color: colors.textSecondary, fontSize: '12px', display: 'block', marginBottom: '4px' }}>Name</label>
              <input
                type="text"
                value={manualStoreyName}
                onChange={(e) => setManualStoreyName(e.target.value)}
                placeholder="e.g. Ground Floor"
                style={{
                  width: '100%', padding: '8px 12px', borderRadius: '6px',
                  border: `1px solid ${colors.border}`, backgroundColor: colors.background,
                  color: colors.textPrimary, fontSize: '13px', boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ color: colors.textSecondary, fontSize: '12px', display: 'block', marginBottom: '4px' }}>Bottom (m)</label>
                <input
                  type="number"
                  step="0.01"
                  value={manualStoreyBottom}
                  onChange={(e) => setManualStoreyBottom(e.target.value)}
                  placeholder="0.00"
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: '6px',
                    border: `1px solid ${colors.border}`, backgroundColor: colors.background,
                    color: colors.textPrimary, fontSize: '13px', boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ color: colors.textSecondary, fontSize: '12px', display: 'block', marginBottom: '4px' }}>Top (m)</label>
                <input
                  type="number"
                  step="0.01"
                  value={manualStoreyTop}
                  onChange={(e) => setManualStoreyTop(e.target.value)}
                  placeholder="3.50"
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: '6px',
                    border: `1px solid ${colors.border}`, backgroundColor: colors.background,
                    color: colors.textPrimary, fontSize: '13px', boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>
            {manualStoreyBottom && manualStoreyTop && !isNaN(parseFloat(manualStoreyTop) - parseFloat(manualStoreyBottom)) && (
              <div style={{ color: colors.textSecondary, fontSize: '12px', marginBottom: '16px' }}>
                Height: {(parseFloat(manualStoreyTop) - parseFloat(manualStoreyBottom)).toFixed(2)}m
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowManualStoreyModal(false); setEditingStoreyLocation(null); }}
                className="btn btn-secondary"
                style={{ padding: '8px 16px', fontSize: '13px' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveManualStorey}
                className="btn btn-primary"
                disabled={!manualStoreyName.trim() || !manualStoreyBottom || !manualStoreyTop || parseFloat(manualStoreyBottom) >= parseFloat(manualStoreyTop)}
                style={{ padding: '8px 16px', fontSize: '13px' }}
              >
                {editingStoreyLocation ? 'Save Changes' : 'Create Storey'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Merge Storeys Modal */}
      {showMergeStoreyModal && (() => {
        const storeys = locations.filter(l => l.type === 'Building Storey');
        // Helper: get storey elevation from elevation, position_z, or position.z
        const getStoreyElev = (s: any) => {
          if (s.elevation != null && String(s.elevation) !== '0' && String(s.elevation) !== '') return parseFloat(String(s.elevation));
          if (s.position_z != null) return parseFloat(String(s.position_z));
          if (s.position?.z != null) return parseFloat(String(s.position.z));
          return 0;
        };
        const getStoreyHeight = (s: any) => {
          if (s.height != null && parseFloat(String(s.height)) > 0) return parseFloat(String(s.height));
          // Estimate height from next storey above
          const sortedStoreys = [...storeys].sort((a, b) => getStoreyElev(a) - getStoreyElev(b));
          const idx = sortedStoreys.findIndex(st => st.id === s.id);
          if (idx >= 0 && idx < sortedStoreys.length - 1) {
            return getStoreyElev(sortedStoreys[idx + 1]) - getStoreyElev(s);
          }
          return 3; // Default 3m height for topmost storey
        };
        const selectedStoreysList = storeys.filter(s => selectedMergeStoreys.has(s.id));
        const computedBottom = selectedStoreysList.length >= 2
          ? Math.min(...selectedStoreysList.map(s => getStoreyElev(s)))
          : null;
        const computedTop = selectedStoreysList.length >= 2
          ? Math.max(...selectedStoreysList.map(s => getStoreyElev(s) + getStoreyHeight(s)))
          : null;

        return (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 'var(--dt-z-modal)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }} onClick={() => setShowMergeStoreyModal(false)}>
            <div style={{
              backgroundColor: colors.background, borderRadius: '12px',
              padding: '24px', minWidth: '400px', maxWidth: '480px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)', border: `1px solid ${colors.border}`
            }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 16px 0', color: colors.textPrimary, fontSize: '16px' }}>
                Merge Storeys
              </h3>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: colors.textSecondary, fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Select storeys to merge (2 or more):
                </label>
                <div style={{
                  maxHeight: '200px', overflowY: 'auto',
                  border: `1px solid ${colors.border}`, borderRadius: '6px',
                  padding: '4px'
                }}>
                  {storeys.map(s => {
                    const elev = getStoreyElev(s);
                    const height = getStoreyHeight(s);
                    return (
                      <label
                        key={s.id}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '8px',
                          padding: '8px 10px', cursor: 'pointer', borderRadius: '4px',
                          backgroundColor: selectedMergeStoreys.has(s.id) ? 'rgba(72, 202, 228, 0.1)' : 'transparent'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedMergeStoreys.has(s.id)}
                          onChange={() => {
                            const next = new Set(selectedMergeStoreys);
                            if (next.has(s.id)) next.delete(s.id);
                            else next.add(s.id);
                            setSelectedMergeStoreys(next);
                          }}
                        />
                        <span style={{ color: colors.textPrimary, fontSize: '13px' }}>
                          {s.name}
                        </span>
                        <span style={{ color: colors.textSecondary, fontSize: '11px', marginInlineStart: 'auto' }}>
                          {elev.toFixed(2)}m – {(elev + height).toFixed(2)}m
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
              {computedBottom !== null && computedTop !== null && (
                <div style={{
                  padding: '8px 12px', backgroundColor: 'rgba(72, 202, 228, 0.1)',
                  borderRadius: '6px', marginBottom: '12px', fontSize: '13px', color: colors.textPrimary
                }}>
                  Result: {computedBottom.toFixed(2)}m → {computedTop.toFixed(2)}m (height: {(computedTop - computedBottom).toFixed(2)}m)
                </div>
              )}
              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: colors.textSecondary, fontSize: '12px', display: 'block', marginBottom: '4px' }}>Name</label>
                <input
                  type="text"
                  value={mergeStoreyName}
                  onChange={(e) => setMergeStoreyName(e.target.value)}
                  placeholder="e.g. Merged Ground + Level 1"
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: '6px',
                    border: `1px solid ${colors.border}`, backgroundColor: colors.background,
                    color: colors.textPrimary, fontSize: '13px', boxSizing: 'border-box'
                  }}
                />
              </div>
              <label style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                marginBottom: '16px', cursor: 'pointer', fontSize: '13px', color: colors.textPrimary
              }}>
                <input
                  type="checkbox"
                  checked={destroyOriginStoreys}
                  onChange={(e) => setDestroyOriginStoreys(e.target.checked)}
                />
                Destroy origin storeys
              </label>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowMergeStoreyModal(false)}
                  className="btn btn-secondary"
                  style={{ padding: '8px 16px', fontSize: '13px' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveMergeStorey}
                  className="btn btn-primary"
                  disabled={selectedMergeStoreys.size < 2 || !mergeStoreyName.trim()}
                  style={{ padding: '8px 16px', fontSize: '13px' }}
                >
                  Create Storey
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default LocationsPage;